import { eventLocationKey } from '@app/src/components/event-management/constants/eventModuleKeys';
import AuthStore from '@app/src/common/store/AuthStore';
import { UserPermissions } from '@app/src/common/model/AuthModel';
import LocationRepository from '@app/src/components/event-management/repository/LocationRepository';
import AppStore from '@app/src/common/store/AppStore';
import EventsRepository from '@app/src/components/event-management/repository/EventsRepository';
import ContactsRepository from '@app/src/components/event-management/repository/ContactsRepository';
import DictionaryRepository from '@app/src/components/event-management/repository/DictionaryRepository';
import LocaleStore from '@app/src/common/store/LocaleStore';
import BookingRepository from '@app/src/components/booking-management/repository/BookingRepository';
import { ISelectOption } from '@app/src/common/model/FormField';
import UserManagementRepository from '@app/src/common/repository/UserManagementRepository';
import ResourceRepository from '@app/src/components/user-management/repository/ResourceRepository';

export function initDictionaries(
  names: string[],
  isForm: boolean = false,
  loadParams?: Record<string, any>
) {
  const promises = names
    .map(name => promiseByName(name, isForm, loadParams))
    .filter(item => item);
  return Promise.all(promises).then(results =>
    results.reduce((obj, item) => Object.assign(obj, item), {})
  );
}

function promiseByName(
  name: string,
  isForm: boolean,
  loadParams?: Record<string, any>
) {
  switch (name) {
    case 'locations': {
      const isEventLocation = AppStore.configurablePages.get(eventLocationKey);
      const canFetchLocations = AuthStore.userPermissions
        .slice()
        .includes(UserPermissions.eventmanagement_view_location_company);
      if (isEventLocation && canFetchLocations) {
        let params;
        if (isForm) {
          params = { toEditVisits: true } as any;
        }
        return LocationRepository.getLocations(params).then(({ items }) => ({
          locations: items.map(mapToFilterOption)
        }));
      } else {
        return Promise.resolve({});
      }
    }
    case 'visitorTypeNames': {
      return EventsRepository.getVisitorTypes().then(({ items }) => ({
        visitorTypeNames: items.map(item => ({
          value: item.name,
          label: item.name
        }))
      }));
    }
    case 'visitTypes': {
      return EventsRepository.getVisitTypes({ active: true }).then(
        ({ items }) => ({
          visitTypes: items.map(item => ({
            value: item.shortName,
            label: `${item.shortName} - ${item.name}`
          }))
        })
      );
    }
    case 'visitTypesSelect': {
      return EventsRepository.getVisitTypes({ active: true }).then(
        ({ items }) => ({
          visitTypes: items.map(item => ({
            value: item.id,
            label: `${item.shortName} - ${item.name}`
          }))
        })
      );
    }
    case 'contacts': {
      const params = { ...(loadParams ?? {}), includeAssets: isForm };
      return ContactsRepository.getContacts(params).then(({ items }) => ({
        contacts: items.map(item => ({
          value: item.id,
          label: item.fullName || item.email,
          option: item
        }))
      }));
    }
    case 'companies': {
      const params: any = { ...(loadParams ?? {}), includeDeleted: !isForm };
      return EventsRepository.getVisitorCompanies(params).then(({ items }) => ({
        companies: items.map(item => ({
          value: item.id,
          label: item.name,
          option: item
        }))
      }));
    }
    case 'rooms': {
      return BookingRepository.getRoomNames().then(items => ({
        rooms: items.map(mapToFilterOption)
      }));
    }
    case 'bookingLocations': {
      return BookingRepository.getLocations().then(({ data }) => ({
        bookingLocations: data.items.map(mapToFilterOption)
      }));
    }
    case 'tenants': {
      const canFetchTenants = AuthStore.hasPermission(
        UserPermissions.access_all_tenant_entities
      );
      if (canFetchTenants) {
        return UserManagementRepository.getCompanyNames().then(({ data }) => ({
          tenants: data.map(mapToFilterOption)
        }));
      } else {
        return Promise.resolve({});
      }
    }
    case 'companyResources': {
      const companyId = loadParams?.companyId ?? AuthStore.companyId;
      return ResourceRepository.getResourceNames(companyId).then(data => ({
        companyResources: data.map(item => ({
          value: item.id,
          label: item.path
        }))
      }));
    }
    default: {
      if (name.startsWith('dictionary')) {
        const [_, key] = name.split(':');
        return DictionaryRepository.getDictionaryItemsByKey(key, {
          includeDeleted: true,
          languageCode: LocaleStore.locale
        }).then(items => ({
          [key]: items.map(item => ({
            value: item.key,
            label: item.translations[0]?.translation || item.key
          }))
        }));
      } else {
        return Promise.resolve({});
      }
    }
  }
}

export function reloadDictionary(name: string, params?: Record<string, any>) {
  return promiseByName(name, true, params).then(result => result || {});
}

export const mapToFilterOption = ({
  id,
  name
}: {
  id: string;
  name: string;
}): ISelectOption => ({
  value: id,
  label: name
});
